import {useCallback, useEffect, useRef} from 'react';
import {create} from 'zustand';
import {ActiveSelection, Canvas, FabricObject, Point} from 'fabric';
import {
    PAN_STEP,
    SNAP_SIZE,
    ZOOM_MAX,
    ZOOM_MIN,
    ZOOM_STEP
} from "../utils/constants";
import {useFloorplanContext} from "./useFloorplanContext";
import {getGridMetrics, snapObjectAnchorPoint} from "../utils/snap-geometry";

export const useViewportStore = create<{
    zoom: number;
    panX: number;
    panY: number;
}>(() => ({zoom: 100, panX: 0, panY: 0}));

export const fitCanvasToContainer = (canvas: Canvas, container: HTMLElement) => {
    const containerWidth = Math.max(container.clientWidth, 100);
    const containerHeight = Math.max(container.clientHeight, 100);

    if (canvas.width !== containerWidth || canvas.height !== containerHeight) {
        canvas.setWidth(containerWidth);
        canvas.setHeight(containerHeight);
    }
    canvas.calcOffset();

    const objects = canvas.getObjects();
    if (objects.length === 0) {
        canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        canvas.renderAll();
        useViewportStore.setState({zoom: 100, panX: 0, panY: 0});
        return;
    }

    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    objects.forEach(object => {
        const bounds = object.getBoundingRect();
        minX = Math.min(minX, bounds.left);
        minY = Math.min(minY, bounds.top);
        maxX = Math.max(maxX, bounds.left + bounds.width);
        maxY = Math.max(maxY, bounds.top + bounds.height);
    });

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    if (contentWidth <= 0 || contentHeight <= 0) {
        canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
        canvas.renderAll();
        useViewportStore.setState({zoom: 100, panX: 0, panY: 0});
        return;
    }

    const padding = 50;
    const scale = Math.min(
        Math.max(containerWidth - padding * 2, 1) / contentWidth,
        Math.max(containerHeight - padding * 2, 1) / contentHeight,
        2,
    );
    const zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, scale * 100));
    const zoomFactor = zoom / 100;
    const panX = containerWidth / 2 - ((minX + maxX) / 2) * zoomFactor;
    const panY = containerHeight / 2 - ((minY + maxY) / 2) * zoomFactor;

    canvas.setViewportTransform([zoomFactor, 0, 0, zoomFactor, panX, panY]);
    canvas.renderAll();
    useViewportStore.setState({zoom, panX, panY});
};

export const useViewportControls = () => {
    const {canvas, containerRef, mode, restaurantId} = useFloorplanContext();
    const zoom = useViewportStore(s => s.zoom);
    const panX = useViewportStore(s => s.panX);
    const panY = useViewportStore(s => s.panY);

    const isEditMode = mode === 'edit';

    // Track which restaurantId we already fit-to-view for
    const fittedForRestaurantRef = useRef<string | null>(null);

    // Panning state
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const isPanningRef = useRef(false);
    const isSpaceDownRef = useRef(false);
    const lastPanPointRef = useRef({x: 0, y: 0});

    // Grid calculations (only relevant in edit mode, hidden until initial fit-to-view completes)
    const hasFitted = fittedForRestaurantRef.current === restaurantId;
    const gridOpacity = Math.min(0.3, Math.max(0.05, (zoom / 100) * 0.3));
    const gridMetrics = getGridMetrics(zoom, panX, panY);
    const gridStyle = isEditMode && hasFitted ? {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none' as const,
        zIndex: 0,
        opacity: gridOpacity,
        backgroundImage: `
            linear-gradient(to right, rgba(140, 140, 140, 0.8) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(140, 140, 140, 0.8) 1px, transparent 1px)
        `,
        backgroundSize: `
            ${gridMetrics.majorSize}px ${gridMetrics.majorSize}px,
            ${gridMetrics.majorSize}px ${gridMetrics.majorSize}px
        `,
        backgroundPosition: `
            ${gridMetrics.majorOffsetX}px ${gridMetrics.majorOffsetY}px,
            ${gridMetrics.majorOffsetX}px ${gridMetrics.majorOffsetY}px
        `,
    } : null;

    const applyTransform = useCallback((newZoom: number, newPanX: number, newPanY: number) => {
        if (!canvas) return;
        const zoomFactor = newZoom / 100;
        canvas.setViewportTransform([zoomFactor, 0, 0, zoomFactor, newPanX, newPanY]);
        canvas.renderAll();
    }, [canvas]);

    const zoomAtPoint = useCallback((delta: number, pointX: number, pointY: number) => {
        const current = useViewportStore.getState();
        const oldZoom = current.zoom / 100;
        const newZoomPercent = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, current.zoom + delta));
        const newZoom = newZoomPercent / 100;

        const zoomRatio = newZoom / oldZoom;
        const newPanX = pointX - (pointX - current.panX) * zoomRatio;
        const newPanY = pointY - (pointY - current.panY) * zoomRatio;

        applyTransform(newZoomPercent, newPanX, newPanY);
    }, [applyTransform]);

    const zoomIn = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const centerX = container.clientWidth / 2;
        const centerY = container.clientHeight / 2;
        zoomAtPoint(ZOOM_STEP, centerX, centerY);
    }, [containerRef, zoomAtPoint]);

    const zoomOut = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const centerX = container.clientWidth / 2;
        const centerY = container.clientHeight / 2;
        zoomAtPoint(-ZOOM_STEP, centerX, centerY);
    }, [containerRef, zoomAtPoint]);

    const pan = useCallback((dx: number, dy: number) => {
        const current = useViewportStore.getState();
        applyTransform(current.zoom, current.panX + dx, current.panY + dy);
    }, [applyTransform]);

    const resetView = useCallback(() => {
        applyTransform(100, 0, 0);
    }, [applyTransform]);

    const fitToView = useCallback(() => {
        const container = containerRef.current;
        if (!canvas || !container) return;

        fitCanvasToContainer(canvas, container);
    }, [canvas, containerRef]);

    // Snap-to-grid logic for live moves only.
    useEffect(() => {
        if (!canvas || !isEditMode) return;

        const handleObjectMoving = (e: any) => {
            const obj = e.target as FabricObject | undefined;
            if (!obj) return;

            const evt = e.e as MouseEvent | undefined;
            if (evt && (evt.shiftKey || evt.ctrlKey || evt.metaKey)) {
                return;
            }

            if (obj instanceof ActiveSelection) return;

            snapObjectAnchorPoint(obj);
        };

        canvas.on('object:moving', handleObjectMoving);
        return () => canvas.off('object:moving', handleObjectMoving);
    }, [canvas, isEditMode]);

    // One-time fit-to-view when restaurant changes
    const fitToViewRef = useRef(fitToView);
    fitToViewRef.current = fitToView;

    useEffect(() => {
        if (!canvas || fittedForRestaurantRef.current === restaurantId) return;

        const handler = () => {
            if (canvas.getObjects().length > 0) {
                canvas.off('after:render', handler);
                fittedForRestaurantRef.current = restaurantId;
                fitToViewRef.current();
            }
        };

        canvas.on('after:render', handler);

        return () => {
            canvas.off('after:render', handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas, restaurantId]);

    // Setup all event listeners
    useEffect(() => {
        const container = containerRef.current;
        if (!canvas || !container) return;

        const originalPosition = container.style.position;
        if (!originalPosition || originalPosition === 'static') {
            container.style.position = 'relative';
        }

        const originalOverscrollBehavior = container.style.overscrollBehavior;
        const originalTouchAction = container.style.touchAction;
        container.style.overscrollBehaviorX = 'none';
        container.style.overscrollBehaviorY = 'none';
        container.style.touchAction = 'none';

        const overlay = document.createElement('div');
        overlay.className = 'viewport-pan-overlay';
        overlay.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1000;
            cursor: grab;
            display: none;
        `;
        container.appendChild(overlay);
        overlayRef.current = overlay;

        const panHandler = (dx: number, dy: number) => {
            const current = useViewportStore.getState();
            const zoomFactor = current.zoom / 100;
            canvas.setViewportTransform([zoomFactor, 0, 0, zoomFactor, current.panX + dx, current.panY + dy]);
            canvas.renderAll();
        };

        const zoomAtPointHandler = (delta: number, pointX: number, pointY: number) => {
            const current = useViewportStore.getState();
            const oldZoom = current.zoom / 100;
            const newZoomPercent = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, current.zoom + delta));
            const newZoom = newZoomPercent / 100;
            const zoomRatio = newZoom / oldZoom;
            const newPanX = pointX - (pointX - current.panX) * zoomRatio;
            const newPanY = pointY - (pointY - current.panY) * zoomRatio;

            canvas.setViewportTransform([newZoom, 0, 0, newZoom, newPanX, newPanY]);
            canvas.renderAll();
        };

        const zoomInHandler = () => {
            const centerX = container.clientWidth / 2;
            const centerY = container.clientHeight / 2;
            zoomAtPointHandler(ZOOM_STEP, centerX, centerY);
        };

        const zoomOutHandler = () => {
            const centerX = container.clientWidth / 2;
            const centerY = container.clientHeight / 2;
            zoomAtPointHandler(-ZOOM_STEP, centerX, centerY);
        };

        const handleOverlayMouseDown = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            isPanningRef.current = true;
            lastPanPointRef.current = {x: e.clientX, y: e.clientY};
            overlay.style.cursor = 'grabbing';
        };

        const handleOverlayMouseMove = (e: MouseEvent) => {
            if (!isPanningRef.current) return;
            e.preventDefault();
            const dx = e.clientX - lastPanPointRef.current.x;
            const dy = e.clientY - lastPanPointRef.current.y;
            lastPanPointRef.current = {x: e.clientX, y: e.clientY};
            panHandler(dx, dy);
        };

        const handleOverlayMouseUp = () => {
            if (!isPanningRef.current) return;
            isPanningRef.current = false;
            overlay.style.cursor = 'grab';
        };

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const rect = container.getBoundingClientRect();
            const pointX = e.clientX - rect.left;
            const pointY = e.clientY - rect.top;

            const absDeltaX = Math.abs(e.deltaX);
            const absDeltaY = Math.abs(e.deltaY);

            const isPinchZoom = e.ctrlKey && absDeltaY > 2;
            const isPanMode = e.shiftKey;

            if (isPinchZoom) {
                const sensitivity = 0.5;
                const delta = -e.deltaY * sensitivity;
                zoomAtPointHandler(delta, pointX, pointY);
            } else if (isPanMode) {
                panHandler(-e.deltaX, -e.deltaY);
            } else {
                const isMainlyHorizontal = absDeltaX > absDeltaY * 1.5;

                if (isMainlyHorizontal) {
                    panHandler(-e.deltaX, 0);
                } else {
                    const sensitivity = absDeltaY < 10 ? 0.15 : 0.3;
                    const delta = -e.deltaY * sensitivity;
                    zoomAtPointHandler(delta, pointX, pointY);
                }
            }
        };

        let touchStart: { distance: number; center: Point } | null = null;

        const getTouchInfo = (touches: TouchList) => {
            if (touches.length < 2) return null;
            const t1 = touches[0];
            const t2 = touches[1];
            const distance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
            const center = new Point((t1.clientX + t2.clientX) / 2, (t1.clientY + t2.clientY) / 2);
            return {distance, center};
        };

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 2) return;
            e.preventDefault();
            touchStart = getTouchInfo(e.touches);
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length !== 2 || !touchStart) return;
            e.preventDefault();
            const current = getTouchInfo(e.touches);
            if (!current) return;

            const rect = container.getBoundingClientRect();
            const pointX = current.center.x - rect.left;
            const pointY = current.center.y - rect.top;

            const scale = current.distance / touchStart.distance;
            const zoomDelta = (scale - 1) * 50;
            const dx = current.center.x - touchStart.center.x;
            const dy = current.center.y - touchStart.center.y;

            zoomAtPointHandler(zoomDelta, pointX, pointY);
            panHandler(dx, dy);

            touchStart = current;
        };

        const handleTouchEnd = () => {
            touchStart = null;
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }

            if (e.code === 'Space' && !e.repeat) {
                e.preventDefault();
                isSpaceDownRef.current = true;
                overlay.style.display = 'block';
                return;
            }

            if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
                if (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd') {
                    e.preventDefault();
                    zoomInHandler();
                    return;
                }

                if (e.key === '-' || e.key === '_' || e.code === 'NumpadSubtract') {
                    e.preventDefault();
                    zoomOutHandler();
                    return;
                }
            }

            // Shift + Arrow: nudge selected element(s); plain Arrow: pan viewport
            const isArrow = e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight';
            if (isArrow && e.shiftKey && isEditMode && canvas) {
                const activeObj = canvas.getActiveObject();
                if (activeObj) {
                    e.preventDefault();
                    const step = e.altKey ? 1 : SNAP_SIZE;
                    const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
                    const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
                    activeObj.set({
                        left: (activeObj.left ?? 0) + dx,
                        top: (activeObj.top ?? 0) + dy,
                    });
                    activeObj.setCoords();
                    canvas.requestRenderAll();
                    canvas.fire('object:modified', {target: activeObj});
                    return;
                }
            }

            switch (e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    panHandler(0, PAN_STEP);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    panHandler(0, -PAN_STEP);
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    panHandler(PAN_STEP, 0);
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    panHandler(-PAN_STEP, 0);
                    break;
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.code !== 'Space') return;
            e.preventDefault();
            isSpaceDownRef.current = false;
            isPanningRef.current = false;
            overlay.style.display = 'none';
        };

        const handleMouseDown = (e: MouseEvent) => {
            if (e.button !== 1) return;
            e.preventDefault();
            isPanningRef.current = true;
            lastPanPointRef.current = {x: e.clientX, y: e.clientY};
            container.style.cursor = 'grabbing';
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (!isPanningRef.current || isSpaceDownRef.current) return;
            e.preventDefault();
            const dx = e.clientX - lastPanPointRef.current.x;
            const dy = e.clientY - lastPanPointRef.current.y;
            lastPanPointRef.current = {x: e.clientX, y: e.clientY};
            panHandler(dx, dy);
        };

        const handleMouseUp = () => {
            if (!isPanningRef.current || isSpaceDownRef.current) return;
            isPanningRef.current = false;
            container.style.cursor = '';
        };

        const handleContextMenu = (e: MouseEvent) => {
            if (e.button === 1) e.preventDefault();
        };

        // Keep the store in sync with the actual canvas transform so that
        // external viewport changes (e.g. auto-pan, sidebar locate) are picked up.
        const syncStoreFromCanvas = () => {
            const vt = canvas.viewportTransform;
            if (!vt) return;
            const current = useViewportStore.getState();
            const newZoom = vt[0] * 100;
            const newPanX = vt[4];
            const newPanY = vt[5];
            if (current.zoom === newZoom && current.panX === newPanX && current.panY === newPanY) return;
            useViewportStore.setState({
                zoom: newZoom,
                panX: newPanX,
                panY: newPanY
            });
        };

        canvas.on('after:render', syncStoreFromCanvas);

        overlay.addEventListener('mousedown', handleOverlayMouseDown);
        overlay.addEventListener('mousemove', handleOverlayMouseMove);
        overlay.addEventListener('mouseup', handleOverlayMouseUp);
        overlay.addEventListener('mouseleave', handleOverlayMouseUp);

        container.addEventListener('wheel', handleWheel, {passive: false});
        container.addEventListener('touchstart', handleTouchStart, {passive: false});
        container.addEventListener('touchmove', handleTouchMove, {passive: false});
        container.addEventListener('touchend', handleTouchEnd);
        container.addEventListener('mousedown', handleMouseDown);
        container.addEventListener('contextmenu', handleContextMenu);

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            canvas.off('after:render', syncStoreFromCanvas);

            overlay.removeEventListener('mousedown', handleOverlayMouseDown);
            overlay.removeEventListener('mousemove', handleOverlayMouseMove);
            overlay.removeEventListener('mouseup', handleOverlayMouseUp);
            overlay.removeEventListener('mouseleave', handleOverlayMouseUp);

            if (overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
            overlayRef.current = null;

            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
            container.removeEventListener('mousedown', handleMouseDown);
            container.removeEventListener('contextmenu', handleContextMenu);

            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);

            if (!originalPosition || originalPosition === 'static') {
                container.style.position = originalPosition;
            }

            container.style.overscrollBehavior = originalOverscrollBehavior;
            container.style.touchAction = originalTouchAction;
        };
    }, [canvas, containerRef]);

    return {
        zoom,
        zoomIn,
        zoomOut,
        pan,
        resetView,
        fitToView,
        gridStyle,
        isEditMode,
    };
};
